import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import UkDateField from '@/components/UkDateField';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { calculateAgeFromDateOfBirth, formatAgeLabel, formatUkDateOfBirth } from '../../../ui-foundation/features/family';
import { HEALTH_PET_TYPE_OPTIONS, formatPetTypeLabel } from '../../../ui-foundation/features/health';
import {
  buildPetProfilePayload,
  buildPetProfileSummary,
  EMPTY_SHARED_PET_PROFILE_DRAFT,
  type SharedPetProfileDraft,
} from '../../../ui-foundation/features/pets';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppNativeSelect,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogFooter as DialogFooter,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
} from '../../../ui-foundation/adapters/web';

type ChildRow = Database['public']['Tables']['children']['Row'];
type AdultDependantRow = Database['public']['Tables']['adult_dependants']['Row'];
type PetRow = Database['public']['Tables']['pets']['Row'];

type ChildFormState = {
  dateOfBirth: string;
  nhsNumber: string;
  gpSurgery: string;
  allergies: string;
  medications: string;
  vaccinationNotes: string;
  schoolName: string;
  keyContacts: string;
  emergencyContacts: string;
  documentsSummary: string;
  notes: string;
};

type AdultFormState = {
  name: string;
  relationship: string;
  dateOfBirth: string;
  nhsNumber: string;
  gpSurgery: string;
  careNeedsSummary: string;
  medications: string;
  careProviderDetails: string;
  powerOfAttorneyStatus: string;
  benefitEntitlements: string;
  emergencyContacts: string;
  notes: string;
};

type PetFormState = SharedPetProfileDraft;

const EMPTY_CHILD_FORM: ChildFormState = {
  dateOfBirth: '',
  nhsNumber: '',
  gpSurgery: '',
  allergies: '',
  medications: '',
  vaccinationNotes: '',
  schoolName: '',
  keyContacts: '',
  emergencyContacts: '',
  documentsSummary: '',
  notes: '',
};

const EMPTY_ADULT_FORM: AdultFormState = {
  name: '',
  relationship: '',
  dateOfBirth: '',
  nhsNumber: '',
  gpSurgery: '',
  careNeedsSummary: '',
  medications: '',
  careProviderDetails: '',
  powerOfAttorneyStatus: '',
  benefitEntitlements: '',
  emergencyContacts: '',
  notes: '',
};

const EMPTY_PET_FORM: PetFormState = EMPTY_SHARED_PET_PROFILE_DRAFT;

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function childHasDetails(child: ChildRow) {
  return Boolean(
    child.date_of_birth
    || child.nhs_number
    || child.gp_surgery
    || child.allergies
    || child.medications
    || child.vaccination_notes
    || child.school_name
    || child.key_contacts
    || child.emergency_contacts
    || child.documents_summary
    || child.notes
  );
}

function childSummary(child: ChildRow) {
  const parts = [
    child.date_of_birth ? `DOB ${formatUkDateOfBirth(child.date_of_birth)}` : null,
    child.school_name ? `School: ${child.school_name}` : null,
    child.gp_surgery ? `GP: ${child.gp_surgery}` : null,
    child.emergency_contacts ? 'Emergency contact saved' : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'No detailed profile saved yet.';
}

function adultSummary(profile: AdultDependantRow) {
  const parts = [
    profile.relationship || null,
    formatAgeLabel(profile.date_of_birth),
    profile.care_needs_summary ? 'Care summary saved' : null,
    profile.care_provider_details ? 'Care provider saved' : null,
    profile.emergency_contacts ? 'Emergency contact saved' : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'No detailed profile saved yet.';
}

function petSummary(pet: PetRow) {
  const parts = buildPetProfileSummary({
    petType: pet.pet_type,
    breed: pet.breed,
    dateOfBirth: pet.dob,
    ageNotes: pet.age_notes,
    microchipNumber: pet.microchip_number,
    adoptionDate: pet.adoption_date,
    weightKg: pet.weight_kg,
    healthConditionsSummary: pet.health_conditions_summary,
    insuranceSummary: pet.insurance_summary,
    vetPracticeSummary: pet.vet_practice_summary,
  });

  return parts.length > 0 ? parts.join(' · ') : 'No detailed profile saved yet.';
}

export default function FamilyProfilesSection({
  surface = 'family',
}: {
  surface?: 'family' | 'profile';
}) {
  const navigate = useNavigate();
  const { user } = useApp();
  const [children, setChildren] = useState<ChildRow[]>([]);
  const [adultDependants, setAdultDependants] = useState<AdultDependantRow[]>([]);
  const [pets, setPets] = useState<PetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [adultDialogOpen, setAdultDialogOpen] = useState(false);
  const [petDialogOpen, setPetDialogOpen] = useState(false);
  const [editingChild, setEditingChild] = useState<ChildRow | null>(null);
  const [editingAdult, setEditingAdult] = useState<AdultDependantRow | null>(null);
  const [editingPet, setEditingPet] = useState<PetRow | null>(null);
  const [childForm, setChildForm] = useState<ChildFormState>(EMPTY_CHILD_FORM);
  const [adultForm, setAdultForm] = useState<AdultFormState>(EMPTY_ADULT_FORM);
  const [petForm, setPetForm] = useState<PetFormState>(EMPTY_PET_FORM);
  const [savingChild, setSavingChild] = useState(false);
  const [savingAdult, setSavingAdult] = useState(false);
  const [savingPet, setSavingPet] = useState(false);
  const [deletingAdult, setDeletingAdult] = useState(false);
  const [deletingPet, setDeletingPet] = useState(false);

  const fetchProfiles = useCallback(async () => {
    if (!user) return;

    const [{ data: childRows }, { data: adultRows }, { data: petRows }] = await Promise.all([
      supabase.from('children').select('*').eq('user_id', user.id).order('name'),
      supabase.from('adult_dependants').select('*').eq('user_id', user.id).order('name'),
      supabase.from('pets').select('*').order('name'),
    ]);

    setChildren(((childRows || []) as ChildRow[]).filter((child) => child.name.trim().length > 0));
    setAdultDependants((adultRows || []) as AdultDependantRow[]);
    setPets((petRows || []) as PetRow[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchProfiles();

    const handleRefresh = () => {
      void fetchProfiles();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [fetchProfiles]);

  const childrenWithDetails = useMemo(
    () => children.filter((child) => childHasDetails(child)).length,
    [children]
  );
  const inProfile = surface === 'profile';

  const openChildEditor = (child: ChildRow) => {
    setEditingChild(child);
    setChildForm({
      dateOfBirth: child.date_of_birth || '',
      nhsNumber: child.nhs_number || '',
      gpSurgery: child.gp_surgery || '',
      allergies: child.allergies || '',
      medications: child.medications || '',
      vaccinationNotes: child.vaccination_notes || '',
      schoolName: child.school_name || '',
      keyContacts: child.key_contacts || '',
      emergencyContacts: child.emergency_contacts || '',
      documentsSummary: child.documents_summary || '',
      notes: child.notes || '',
    });
    setChildDialogOpen(true);
  };

  const resetChildEditor = () => {
    setEditingChild(null);
    setChildForm(EMPTY_CHILD_FORM);
    setChildDialogOpen(false);
  };

  const saveChildProfile = async () => {
    if (!editingChild) return;

    setSavingChild(true);
    const payload = {
      age: calculateAgeFromDateOfBirth(childForm.dateOfBirth || null) ?? editingChild.age,
      date_of_birth: childForm.dateOfBirth || null,
      nhs_number: childForm.nhsNumber.trim() || null,
      gp_surgery: childForm.gpSurgery.trim() || null,
      allergies: childForm.allergies.trim() || null,
      medications: childForm.medications.trim() || null,
      vaccination_notes: childForm.vaccinationNotes.trim() || null,
      school_name: childForm.schoolName.trim() || null,
      school_urn: childForm.schoolName.trim() === (editingChild.school_name || '').trim()
        ? editingChild.school_urn || null
        : null,
      key_contacts: childForm.keyContacts.trim() || null,
      emergency_contacts: childForm.emergencyContacts.trim() || null,
      documents_summary: childForm.documentsSummary.trim() || null,
      notes: childForm.notes.trim() || null,
    };

    await supabase.from('children').update(payload).eq('id', editingChild.id);
    setSavingChild(false);
    toast.success(`${editingChild.name} profile saved.`);
    notifySectionRefresh();
    resetChildEditor();
    void fetchProfiles();
  };

  const openAdultEditor = (adult: AdultDependantRow | null = null) => {
    setEditingAdult(adult);
    setAdultForm(
      adult
        ? {
            name: adult.name || '',
            relationship: adult.relationship || '',
            dateOfBirth: adult.date_of_birth || '',
            nhsNumber: adult.nhs_number || '',
            gpSurgery: adult.gp_surgery || '',
            careNeedsSummary: adult.care_needs_summary || '',
            medications: adult.medications || '',
            careProviderDetails: adult.care_provider_details || '',
            powerOfAttorneyStatus: adult.power_of_attorney_status || '',
            benefitEntitlements: adult.benefit_entitlements || '',
            emergencyContacts: adult.emergency_contacts || '',
            notes: adult.notes || '',
          }
        : EMPTY_ADULT_FORM
    );
    setAdultDialogOpen(true);
  };

  const resetAdultEditor = () => {
    setEditingAdult(null);
    setAdultForm(EMPTY_ADULT_FORM);
    setAdultDialogOpen(false);
  };

  const saveAdultProfile = async () => {
    if (!user || !adultForm.name.trim()) return;

    setSavingAdult(true);
    const payload = {
      user_id: user.id,
      name: adultForm.name.trim(),
      relationship: adultForm.relationship.trim() || '',
      date_of_birth: adultForm.dateOfBirth || null,
      nhs_number: adultForm.nhsNumber.trim() || null,
      gp_surgery: adultForm.gpSurgery.trim() || null,
      care_needs_summary: adultForm.careNeedsSummary.trim() || null,
      medications: adultForm.medications.trim() || null,
      care_provider_details: adultForm.careProviderDetails.trim() || null,
      power_of_attorney_status: adultForm.powerOfAttorneyStatus.trim() || null,
      benefit_entitlements: adultForm.benefitEntitlements.trim() || null,
      emergency_contacts: adultForm.emergencyContacts.trim() || null,
      notes: adultForm.notes.trim() || null,
    };

    if (editingAdult) {
      await supabase.from('adult_dependants').update(payload).eq('id', editingAdult.id);
      toast.success(`${adultForm.name.trim()} profile saved.`);
    } else {
      await supabase.from('adult_dependants').insert(payload);
      toast.success(`${adultForm.name.trim()} added.`);
    }

    setSavingAdult(false);
    notifySectionRefresh();
    resetAdultEditor();
    void fetchProfiles();
  };

  const openPetEditor = (pet: PetRow | null = null) => {
    setEditingPet(pet);
    setPetForm(
      pet
        ? {
            name: pet.name || '',
            petType: pet.pet_type || 'dog',
            breed: pet.breed || '',
            dateOfBirth: pet.dob || '',
            ageNotes: pet.age_notes || '',
            microchipNumber: pet.microchip_number || '',
            adoptionDate: pet.adoption_date || '',
            weightKg: pet.weight_kg == null ? '' : String(pet.weight_kg),
            healthConditionsSummary: pet.health_conditions_summary || '',
            insuranceSummary: pet.insurance_summary || '',
            vetPracticeSummary: pet.vet_practice_summary || '',
            notes: pet.notes || '',
          }
        : EMPTY_PET_FORM
    );
    setPetDialogOpen(true);
  };

  const resetPetEditor = () => {
    setEditingPet(null);
    setPetForm(EMPTY_PET_FORM);
    setPetDialogOpen(false);
  };

  const savePetProfile = async () => {
    if (!user || !petForm.name.trim() || !petForm.petType.trim()) return;

    setSavingPet(true);
    const payload = buildPetProfilePayload({
      userId: user.id,
      draft: petForm,
    });

    if (editingPet) {
      await supabase.from('pets').update(payload).eq('id', editingPet.id);
      toast.success(`${petForm.name.trim()} profile saved.`);
    } else {
      await supabase.from('pets').insert(payload);
      toast.success(`${petForm.name.trim()} added.`);
    }

    setSavingPet(false);
    notifySectionRefresh();
    resetPetEditor();
    void fetchProfiles();
  };

  const deletePetProfile = async () => {
    if (!editingPet) return;

    setDeletingPet(true);
    try {
      const { error } = await supabase.from('pets').delete().eq('id', editingPet.id);
      if (error) {
        throw error;
      }
      toast.success(`${editingPet.name} removed.`);
      notifySectionRefresh();
      resetPetEditor();
      void fetchProfiles();
    } catch (error: any) {
      toast.error(error?.message || 'Could not remove that pet right now.');
    } finally {
      setDeletingPet(false);
    }
  };

  const deleteAdultProfile = async () => {
    if (!editingAdult) return;

    setDeletingAdult(true);
    await supabase.from('adult_dependants').delete().eq('id', editingAdult.id);
    setDeletingAdult(false);
    toast.success(`${editingAdult.name} removed.`);
    notifySectionRefresh();
    resetAdultEditor();
    void fetchProfiles();
  };

  return (
    <>
      <AppCard>
        <AppSectionHeader
          title="Family profiles"
          subtitle="Keep the practical child and adult-dependant detail you need for school, care, medical appointments, and emergencies."
          action={<Users size={16} className="shrink-0 text-muted-foreground mt-1" />}
        />

        <AppCardBody gap="$lg">
          <div className="grid gap-2 sm:grid-cols-3">
            <MetricPill label="Children with detail" value={`${childrenWithDetails}/${children.length}`} />
            <MetricPill label="Adult dependants" value={`${adultDependants.length}`} />
            <MetricPill label="Pets" value={`${pets.length}`} />
          </div>

          <div>
            <AppSectionHeader
              title="Children"
              subtitle={inProfile
                ? 'Names, dates of birth, and the practical child records all live here in My Settings.'
                : 'Names and dates of birth live in My Settings. Detailed child records live here.'}
              action={inProfile ? undefined : (
                <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                  Open My Settings
                </AppButton>
              )}
            />

            {loading ? (
              <AppLoadingState
                title="Loading family profiles"
                body="Pulling the child detail you already saved."
              />
            ) : children.length === 0 ? (
              <AppEmptyState
                title="No child profiles yet"
                body={inProfile
                  ? 'Add a child above in My Settings, then save the school, GP, medical, and emergency detail here.'
                  : 'Add a child in My Settings first, then save the school, GP, medical, and emergency detail here.'}
                action={inProfile ? undefined : (
                  <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                    Open My Settings
                  </AppButton>
                )}
              />
            ) : (
              <div className="space-y-3 mt-3">
                {children.map((child) => (
                  <AppCard key={child.id} backgroundColor="$background">
                    <AppCardBody>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <AppText fontSize="$sm" fontWeight="700">{child.name}</AppText>
                          <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                            {formatAgeLabel(child.date_of_birth, child.age) || 'No age saved yet'}
                            {child.date_of_birth ? ` · ${formatUkDateOfBirth(child.date_of_birth)}` : ''}
                          </AppText>
                          <AppText className="mt-2 leading-relaxed" fontSize="$sm" color="$colorMuted">{childSummary(child)}</AppText>
                        </div>
                        <AppButton tone="outline" size="sm" onPress={() => openChildEditor(child)}>
                          <Pencil size={12} />
                          {childHasDetails(child) ? 'Edit details' : 'Add details'}
                        </AppButton>
                      </div>
                    </AppCardBody>
                  </AppCard>
                ))}
              </div>
            )}
          </div>

          <div>
            <AppSectionHeader
              title="Adult dependants"
              subtitle="Parents, siblings, partners, or other adults whose care or admin you help manage."
              action={(
                <AppButton size="sm" onPress={() => openAdultEditor()}>
                  <Plus size={14} />
                  Add adult
                </AppButton>
              )}
            />

            {loading ? null : adultDependants.length === 0 ? (
              <AppEmptyState
                title="No adult dependant profiles yet"
                body="Add the one person whose care detail you would most need in a crisis."
              />
            ) : (
              <div className="space-y-3 mt-3">
                {adultDependants.map((adult) => (
                  <AppCard key={adult.id} backgroundColor="$background">
                    <AppCardBody>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <AppText fontSize="$sm" fontWeight="700">{adult.name}</AppText>
                          <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                            {adult.relationship || 'Relationship not set'}
                            {formatAgeLabel(adult.date_of_birth) ? ` · ${formatAgeLabel(adult.date_of_birth)}` : ''}
                            {adult.date_of_birth ? ` · ${formatUkDateOfBirth(adult.date_of_birth)}` : ''}
                          </AppText>
                          <AppText className="mt-2 leading-relaxed" fontSize="$sm" color="$colorMuted">{adultSummary(adult)}</AppText>
                        </div>
                        <AppButton tone="outline" size="sm" onPress={() => openAdultEditor(adult)}>
                          <Pencil size={12} />
                          Edit details
                        </AppButton>
                      </div>
                    </AppCardBody>
                  </AppCard>
                ))}
              </div>
            )}
          </div>

          <div>
            <AppSectionHeader
              title="Pets"
              subtitle={inProfile
                ? 'Add pets here in My Settings so Health reminders and family admin can shape around them.'
                : 'Pet setup now lives in My Settings. Family shows the pets you have already added there.'}
              action={inProfile ? (
                <AppButton size="sm" onPress={() => openPetEditor()}>
                  <Plus size={14} />
                  Add pet
                </AppButton>
              ) : (
                <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                  Open My Settings
                </AppButton>
              )}
            />

            {loading ? null : pets.length === 0 ? (
              <AppEmptyState
                title="No pet profiles yet"
                body={inProfile
                  ? 'Add each pet here in My Settings when you want Health reminders and family admin to include them.'
                  : 'Add each pet in My Settings first, then Family and Health will pick them up automatically.'}
                action={inProfile ? undefined : (
                  <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                    Open My Settings
                  </AppButton>
                )}
              />
            ) : (
              <div className="space-y-3 mt-3">
                {pets.map((pet) => (
                  <AppCard key={pet.id} backgroundColor="$background">
                    <AppCardBody>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <AppText fontSize="$sm" fontWeight="700">{pet.name}</AppText>
                          <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                            {formatPetTypeLabel(pet.pet_type)}
                            {formatAgeLabel(pet.dob) ? ` · ${formatAgeLabel(pet.dob)}` : ''}
                            {pet.dob ? ` · ${formatUkDateOfBirth(pet.dob)}` : ''}
                          </AppText>
                          <AppText className="mt-2 leading-relaxed" fontSize="$sm" color="$colorMuted">{petSummary(pet)}</AppText>
                        </div>
                        <AppButton tone="outline" size="sm" onPress={() => inProfile ? openPetEditor(pet) : navigate('/profile')}>
                          {inProfile ? 'Edit details' : 'Open My Settings'}
                        </AppButton>
                      </div>
                    </AppCardBody>
                  </AppCard>
                ))}
              </div>
            )}
          </div>
        </AppCardBody>
      </AppCard>

      <Dialog open={childDialogOpen} onOpenChange={(open) => { if (!open) resetChildEditor(); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <AppCardBody gap="$md">
            <DialogHeader>
              <DialogTitle>{editingChild ? `Child profile: ${editingChild.name}` : 'Child profile'}</DialogTitle>
              <DialogDescription>
                Save the school, GP, medical, and emergency detail you want close to hand when Family admin gets messy.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-2">
              <UkDateField
                label="Date of birth"
                value={childForm.dateOfBirth}
                onChange={(nextValue) => setChildForm((current) => ({ ...current, dateOfBirth: nextValue }))}
                savedAge={editingChild?.age || null}
              />
              <Field label="NHS number">
                <AppInput
                  value={childForm.nhsNumber}
                  onChange={(event) => setChildForm((current) => ({ ...current, nhsNumber: event.target.value }))}
                  placeholder="Optional"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  spellCheck={false}
                />
              </Field>
              <Field label="GP surgery">
                <AppInput value={childForm.gpSurgery} onChange={(event) => setChildForm((current) => ({ ...current, gpSurgery: event.target.value }))} placeholder="e.g. Riverside Practice" />
              </Field>
              <Field label="School / setting">
                <AppInput value={childForm.schoolName} onChange={(event) => setChildForm((current) => ({ ...current, schoolName: event.target.value }))} placeholder="e.g. Oak Primary" />
              </Field>
            </div>

            <Field label="Allergies">
              <AppTextArea
                value={childForm.allergies}
                onChange={(event) => setChildForm((current) => ({ ...current, allergies: event.target.value }))}
                rows={2}
                placeholder="Food, medicine, or other key allergy detail"
              />
            </Field>

            <Field label="Medications">
              <AppTextArea
                value={childForm.medications}
                onChange={(event) => setChildForm((current) => ({ ...current, medications: event.target.value }))}
                rows={2}
                placeholder="Current medications or regular treatment notes"
              />
            </Field>

            <Field label="Vaccination or health notes">
              <AppTextArea
                value={childForm.vaccinationNotes}
                onChange={(event) => setChildForm((current) => ({ ...current, vaccinationNotes: event.target.value }))}
                rows={2}
                placeholder="Red book reminders, vaccination notes, review dates"
              />
            </Field>

            <Field label="Key contacts">
              <AppTextArea
                value={childForm.keyContacts}
                onChange={(event) => setChildForm((current) => ({ ...current, keyContacts: event.target.value }))}
                rows={2}
                placeholder="Teacher, SENCO, nursery manager, or other practical contacts"
              />
            </Field>

            <Field label="Emergency contacts">
              <AppTextArea
                value={childForm.emergencyContacts}
                onChange={(event) => setChildForm((current) => ({ ...current, emergencyContacts: event.target.value }))}
                rows={2}
                placeholder="Who to call and how"
              />
            </Field>

            <Field label="Documents">
              <AppTextArea
                value={childForm.documentsSummary}
                onChange={(event) => setChildForm((current) => ({ ...current, documentsSummary: event.target.value }))}
                rows={2}
                placeholder="Birth certificate, EHCP, red book, or other important document notes"
              />
            </Field>

            <Field label="Notes">
              <AppTextArea
                value={childForm.notes}
                onChange={(event) => setChildForm((current) => ({ ...current, notes: event.target.value }))}
                rows={3}
                placeholder="Anything else you want ready to hand"
              />
            </Field>

            <DialogFooter>
              <AppButtonRow justifyContent="flex-end">
                <AppButton tone="outline" onPress={resetChildEditor}>Cancel</AppButton>
                <AppButton onPress={saveChildProfile} disabled={savingChild}>
                  {savingChild ? 'Saving…' : 'Save child profile'}
                </AppButton>
              </AppButtonRow>
            </DialogFooter>
          </AppCardBody>
        </DialogContent>
      </Dialog>

      <Dialog open={adultDialogOpen} onOpenChange={(open) => { if (!open) resetAdultEditor(); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <AppCardBody gap="$md">
            <DialogHeader>
              <DialogTitle>{editingAdult ? `Adult dependant: ${editingAdult.name}` : 'Add adult dependant'}</DialogTitle>
              <DialogDescription>
                Keep the care, medication, contact, and power-of-attorney detail you would need quickly in one place.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <AppInput
                  value={adultForm.name}
                  onChange={(event) => setAdultForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="e.g. Mum"
                  autoCapitalize="words"
                  spellCheck={false}
                />
              </Field>
              <Field label="Relationship">
                <AppInput value={adultForm.relationship} onChange={(event) => setAdultForm((current) => ({ ...current, relationship: event.target.value }))} placeholder="e.g. Parent" />
              </Field>
              <UkDateField
                label="Date of birth"
                value={adultForm.dateOfBirth}
                onChange={(nextValue) => setAdultForm((current) => ({ ...current, dateOfBirth: nextValue }))}
              />
              <Field label="NHS number">
                <AppInput
                  value={adultForm.nhsNumber}
                  onChange={(event) => setAdultForm((current) => ({ ...current, nhsNumber: event.target.value }))}
                  placeholder="Optional"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  spellCheck={false}
                />
              </Field>
              <Field label="GP surgery">
                <AppInput value={adultForm.gpSurgery} onChange={(event) => setAdultForm((current) => ({ ...current, gpSurgery: event.target.value }))} placeholder="e.g. Elm Practice" />
              </Field>
              <Field label="Power of attorney">
                <AppInput value={adultForm.powerOfAttorneyStatus} onChange={(event) => setAdultForm((current) => ({ ...current, powerOfAttorneyStatus: event.target.value }))} placeholder="Financial, health, pending, none" />
              </Field>
            </div>

            <Field label="Care needs summary">
              <AppTextArea
                value={adultForm.careNeedsSummary}
                onChange={(event) => setAdultForm((current) => ({ ...current, careNeedsSummary: event.target.value }))}
                rows={3}
                placeholder="Main diagnoses, care needs, or practical support summary"
              />
            </Field>

            <Field label="Medications">
              <AppTextArea
                value={adultForm.medications}
                onChange={(event) => setAdultForm((current) => ({ ...current, medications: event.target.value }))}
                rows={2}
                placeholder="Current medications and dosage notes"
              />
            </Field>

            <Field label="Care provider details">
              <AppTextArea
                value={adultForm.careProviderDetails}
                onChange={(event) => setAdultForm((current) => ({ ...current, careProviderDetails: event.target.value }))}
                rows={2}
                placeholder="Agency, care home, social worker, or support package detail"
              />
            </Field>

            <Field label="Benefit entitlements">
              <AppTextArea
                value={adultForm.benefitEntitlements}
                onChange={(event) => setAdultForm((current) => ({ ...current, benefitEntitlements: event.target.value }))}
                rows={2}
                placeholder="Attendance Allowance, PIP, Pension Credit, or other relevant support"
              />
            </Field>

            <Field label="Emergency contacts">
              <AppTextArea
                value={adultForm.emergencyContacts}
                onChange={(event) => setAdultForm((current) => ({ ...current, emergencyContacts: event.target.value }))}
                rows={2}
                placeholder="Who to call and how"
              />
            </Field>

            <Field label="Notes">
              <AppTextArea
                value={adultForm.notes}
                onChange={(event) => setAdultForm((current) => ({ ...current, notes: event.target.value }))}
                rows={3}
                placeholder="Any other crisis or admin context you want ready"
              />
            </Field>

            <DialogFooter>
              <AppButtonRow justifyContent={editingAdult ? 'space-between' : 'flex-end'} width="100%">
                {editingAdult ? (
                  <AppButton tone="destructive" onPress={deleteAdultProfile} disabled={deletingAdult}>
                    <Trash2 size={14} />
                    {deletingAdult ? 'Removing…' : 'Remove'}
                  </AppButton>
                ) : null}
                <AppButtonRow justifyContent="flex-end">
                  <AppButton tone="outline" onPress={resetAdultEditor}>Cancel</AppButton>
                  <AppButton onPress={saveAdultProfile} disabled={savingAdult || !adultForm.name.trim()}>
                    {savingAdult ? 'Saving…' : editingAdult ? 'Save adult profile' : 'Add adult profile'}
                  </AppButton>
                </AppButtonRow>
              </AppButtonRow>
            </DialogFooter>
          </AppCardBody>
        </DialogContent>
      </Dialog>

      <Dialog open={petDialogOpen} onOpenChange={(open) => { if (!open) resetPetEditor(); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <AppCardBody gap="$md">
            <DialogHeader>
              <DialogTitle>{editingPet ? `Pet profile: ${editingPet.name}` : 'Add pet'}</DialogTitle>
              <DialogDescription>
                Save the basics you need so Adulting can keep pet appointments and medications in the same calm Health view.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <AppInput
                  value={petForm.name}
                  onChange={(event) => setPetForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="e.g. Rosie"
                  spellCheck={false}
                />
              </Field>
              <Field label="Pet type">
                <AppNativeSelect
                  value={petForm.petType}
                  onChange={(event) => setPetForm((current) => ({ ...current, petType: event.target.value }))}
                >
                  {HEALTH_PET_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {formatPetTypeLabel(option)}
                    </option>
                  ))}
                </AppNativeSelect>
              </Field>
              <Field label="Breed">
                <AppInput
                  value={petForm.breed}
                  onChange={(event) => setPetForm((current) => ({ ...current, breed: event.target.value }))}
                  placeholder="Optional"
                />
              </Field>
              <UkDateField
                label="Date of birth"
                value={petForm.dateOfBirth}
                onChange={(nextValue) => setPetForm((current) => ({ ...current, dateOfBirth: nextValue }))}
              />
            </div>

            <Field label="Age notes">
              <AppInput
                value={petForm.ageNotes}
                onChange={(event) => setPetForm((current) => ({ ...current, ageNotes: event.target.value }))}
                placeholder="e.g. Rescue, about 3 years old"
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Microchip number">
                <AppInput
                  value={petForm.microchipNumber}
                  onChange={(event) => setPetForm((current) => ({ ...current, microchipNumber: event.target.value }))}
                  placeholder="Optional"
                />
              </Field>
              <UkDateField
                label="Adoption date"
                value={petForm.adoptionDate}
                onChange={(nextValue) => setPetForm((current) => ({ ...current, adoptionDate: nextValue }))}
              />
              <Field label="Weight (kg)">
                <AppInput
                  value={petForm.weightKg}
                  onChange={(event) => setPetForm((current) => ({ ...current, weightKg: event.target.value }))}
                  placeholder="Optional"
                  inputMode="decimal"
                />
              </Field>
            </div>

            <Field label="Health conditions">
              <AppTextArea
                value={petForm.healthConditionsSummary}
                onChange={(event) => setPetForm((current) => ({ ...current, healthConditionsSummary: event.target.value }))}
                rows={2}
                placeholder="Allergies, conditions, or anything a future vet visit should bring to mind"
              />
            </Field>

            <Field label="Insurance summary">
              <AppInput
                value={petForm.insuranceSummary}
                onChange={(event) => setPetForm((current) => ({ ...current, insuranceSummary: event.target.value }))}
                placeholder="Insurer, policy note, or renewal hint"
              />
            </Field>

            <Field label="Vet practice">
              <AppInput
                value={petForm.vetPracticeSummary}
                onChange={(event) => setPetForm((current) => ({ ...current, vetPracticeSummary: event.target.value }))}
                placeholder="Practice name, phone, or quick note"
              />
            </Field>

            <Field label="Notes">
              <AppTextArea
                value={petForm.notes}
                onChange={(event) => setPetForm((current) => ({ ...current, notes: event.target.value }))}
                rows={3}
                placeholder="Vet, ongoing treatment, behaviour notes, or anything else useful"
              />
            </Field>

            {editingPet ? (
              <AppText fontSize="$xs" color="$colorMuted">
                Removing this pet also removes that pet&apos;s Health reminders and medication tracker items.
              </AppText>
            ) : null}

            <DialogFooter>
              <AppButtonRow justifyContent={editingPet ? 'space-between' : 'flex-end'} width="100%">
                {editingPet && editingPet.user_id === user?.id ? (
                  <AppButton tone="destructive" onPress={deletePetProfile} disabled={deletingPet}>
                    <Trash2 size={14} />
                    {deletingPet ? 'Removing…' : 'Remove'}
                  </AppButton>
                ) : null}
                <AppButtonRow justifyContent="flex-end">
                  <AppButton tone="outline" onPress={resetPetEditor}>Cancel</AppButton>
                  <AppButton onPress={savePetProfile} disabled={savingPet || !petForm.name.trim() || !petForm.petType.trim()}>
                    {savingPet ? 'Saving…' : editingPet ? 'Save pet profile' : 'Add pet'}
                  </AppButton>
                </AppButtonRow>
              </AppButtonRow>
            </DialogFooter>
          </AppCardBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <AppFieldGroup label={label}>{children}</AppFieldGroup>;
}

function MetricPill({ label, value }: { label: string; value: string }) {
  return (
    <AppCard backgroundColor="$background" px="$sm" py="$sm">
      <AppCardBody gap="$xs">
        <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
          {label}
        </AppText>
        <AppText fontSize="$sm" fontWeight="700">{value}</AppText>
      </AppCardBody>
    </AppCard>
  );
}
